import type { PermitTypeContext } from "@offstreetparking/domain/rules";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { AccessibleContent } from "../../accessibility";
import { formatDate, formatDateRange } from "../../formatters";
import { Button } from "../../forms";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
import { DateIcon } from "../../icons/DateIcon";
import { DateRangeIcon } from "../../icons/DateRangeIcon";
import { TimeIcon } from "../../icons/TimeIcon";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { useLocale } from "../../providers/DesignSystemLocaleContext";
import { Text } from "../../typography";
import { classNames } from "../../utils";
import { ParkingTimeSchedule } from "./ParkingTimeSchedule";
import { RulesPreview } from "./RulesPreview";

export type PermitIntervalType = "SINGLE" | "DATE_RANGE";

export const PERMIT_INTERVAL_TYPE = {
	SINGLE: "SINGLE",
	DATE_RANGE: "DATE_RANGE",
} as const;

type PermitIntervals = {
	startDateTimeISO: string;
	endDateTimeISO?: string | null;
	schedules?: {
		startTime: string;
		endTime: string;
		day: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
	}[];
};

type ParkingOptionProps = {
	timezone: string;
	permitIntervals?: PermitIntervals[];
	defaultShowAllIntervals?: boolean;
	isInstant: boolean;
	context?: PermitTypeContext | null;
};

export const ParkingOption = ({
	timezone,
	permitIntervals,
	defaultShowAllIntervals,
	isInstant = false,
	context,
}: ParkingOptionProps) => {
	const { parkingOption } = useDesignSystemI18n();
	const { locale } = useLocale();
	const [showAllIntervals, setShowAllIntervals] = useState(defaultShowAllIntervals ?? false);

	const makeIntervals = useCallback(
		(permitIntervals: PermitIntervals[]) => {
			return (
				permitIntervals
					?.map((interval) => {
						const startDateTime = DateTime.fromISO(interval.startDateTimeISO).setZone(timezone);
						const endDateTime = interval.endDateTimeISO
							? DateTime.fromISO(interval.endDateTimeISO).setZone(timezone)
							: undefined;
						return {
							startDateTimeISO: interval.startDateTimeISO,
							endDateTimeISO: interval.endDateTimeISO ?? undefined,
							type:
								startDateTime.toFormat("yyyy-MM-dd") === endDateTime?.toFormat("yyyy-MM-dd")
									? PERMIT_INTERVAL_TYPE.SINGLE
									: PERMIT_INTERVAL_TYPE.DATE_RANGE,
							schedules: interval.schedules,
						};
					})
					?.reduce(
						(
							acc: {
								key: string;
								type: PermitIntervalType;
								startDateTimeISO: string;
								endDateTimeISO: string | undefined;
								times: { startTime: string; endTime: string | null | undefined }[];
								hasSchedule: boolean;
								schedules?: {
									startTime: string;
									endTime: string;
									day:
										| "MONDAY"
										| "TUESDAY"
										| "WEDNESDAY"
										| "THURSDAY"
										| "FRIDAY"
										| "SATURDAY"
										| "SUNDAY";
								}[];
							}[],
							curr,
						) => {
							const key = `${curr.startDateTimeISO}_${curr.endDateTimeISO}`;

							const currStartDate = DateTime.fromISO(curr.startDateTimeISO)
								.setZone(timezone)
								.toFormat("yyyy-MM-dd");
							const currEndDate = curr.endDateTimeISO
								? DateTime.fromISO(curr.endDateTimeISO).setZone(timezone).toFormat("yyyy-MM-dd")
								: undefined;

							const found = acc.find(
								(item) =>
									DateTime.fromISO(item.startDateTimeISO).setZone(timezone).toFormat("yyyy-MM-dd") ===
										currStartDate &&
									(item.endDateTimeISO
										? DateTime.fromISO(item.endDateTimeISO).setZone(timezone).toFormat("yyyy-MM-dd")
										: undefined) === currEndDate,
							);
							if (found) {
								found.times.push({ startTime: curr.startDateTimeISO, endTime: curr.endDateTimeISO });
							} else {
								acc.push({
									key,
									startDateTimeISO: curr.startDateTimeISO,
									endDateTimeISO: curr.endDateTimeISO,
									type: curr.type,
									times: [{ startTime: curr.startDateTimeISO, endTime: curr.endDateTimeISO }],
									schedules: curr.schedules,
									hasSchedule: (curr.schedules ?? []).length > 0,
								});
							}
							return acc;
						},
						[],
					) ?? []
			);
		},
		[timezone],
	);

	const intervals = makeIntervals(permitIntervals ?? []);

	const shouldCollapse = intervals.length > 3;
	const isCollapsed = shouldCollapse && !showAllIntervals;
	const displayedIntervals = (isCollapsed ? [intervals[0], intervals[intervals.length - 1]] : intervals).filter(
		Boolean,
	);
	const hiddenCount = intervals.length - 2;
	const noIntervals = (permitIntervals ?? []).length === 0;

	return (
		<div className={classNames(isCollapsed ? "print:space-y-4" : "space-y-4")}>
			{noIntervals ? (
				<RulesPreview timezone={timezone} isInstant={isInstant} context={context} />
			) : (
				(isCollapsed ? intervals : displayedIntervals).map((interval, index) => {
					const hasSchedule = interval.schedules && interval.schedules.length > 0;
					const prevInterval = index > 0 ? intervals[index - 1] : null;
					const showDivider = prevInterval && prevInterval.type !== interval.type;
					const showViewMore = isCollapsed && index === 0;

					return (
						<div
							className={classNames(
								"relative",
								isCollapsed && index > 0 && index < intervals.length - 1
									? "hidden print:block"
									: undefined,
							)}
							key={interval.startDateTimeISO}
						>
							{showDivider && <div className="my-4 border-neutral-faded border-t" />}
							{showViewMore && (
								<div className="absolute top-0 left-[7px] mt-6 h-[calc(100%-24px)] border border-neutral-strong border-l border-dashed print:hidden" />
							)}
							<AccessibleContent
								label={announcedInterval({ interval, timezone, isInstant, parkingOption, locale })}
							>
								{interval.type === PERMIT_INTERVAL_TYPE.SINGLE ? (
									<div className="flex items-center gap-4">
										<div className="flex items-center gap-2">
											<DateIcon className="size-4" />
											<Text size="sm" weight="bold">
												{`${formatDate({
													date: interval.startDateTimeISO,
													formatType: "Abbreviated_Date",
													timezone,
												})}`}
											</Text>
										</div>
										{interval.times.map((x) => (
											<div
												key={x.startTime.toString()}
												className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1"
											>
												<TimeIcon className="size-3 min-h-3 min-w-3" />
												<Text size="xs">
													{formatDateRange({
														startDate: x.startTime,
														endDate: x.endTime!,
														formatType: "Abbreviated_Time",
														timezone,
													})}
												</Text>
											</div>
										))}
									</div>
								) : (
									<div className="space-y-2">
										<div className="flex items-start gap-3">
											<DateRangeIcon className="mt-[1px] size-4" />
											<div className="flex items-start gap-4">
												{isInstant ? (
													<div className="flex items-start">
														<Text size="sm" weight="bold">
															{parkingOption.startsNow}
														</Text>
													</div>
												) : (
													<div className="flex flex-col items-start">
														<Text size="sm" weight="bold">
															{`${formatDate({
																date: interval.startDateTimeISO,
																formatType: "Abbreviated_Date",
																timezone,
															})}`}
														</Text>
														{!hasSchedule && (
															<div className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1">
																<TimeIcon className="size-3" />
																<Text size="xs">
																	{formatDate({
																		date: interval.startDateTimeISO,
																		formatType: "Abbreviated_Time",
																		timezone,
																	})}
																</Text>
															</div>
														)}
													</div>
												)}
												<ArrowRightIcon className="mt-[2px] size-4 shrink-0" />
												<div className="flex flex-col items-start">
													<Text size="sm" weight="bold">
														{interval.endDateTimeISO
															? formatDate({
																	date: interval.endDateTimeISO,
																	formatType: "Abbreviated_Date",
																	timezone,
																})
															: parkingOption.noExpiry}
													</Text>
													{!hasSchedule && interval.endDateTimeISO && (
														<div className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1">
															<TimeIcon className="size-3" />
															<Text size="xs">
																{formatDate({
																	date: interval.endDateTimeISO,
																	formatType: "Abbreviated_Time",
																	timezone,
																})}
															</Text>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								)}
							</AccessibleContent>
							{interval.type === PERMIT_INTERVAL_TYPE.DATE_RANGE &&
								interval.schedules &&
								interval.schedules.length > 0 && (
									<div className="mt-2 ml-6">
										<ParkingTimeSchedule
											interval={{
												type: interval.type,
												startDateTimeISO: interval.startDateTimeISO,
												endDateTimeISO: interval.endDateTimeISO,
												schedules: interval.schedules,
											}}
											timezone={timezone}
										/>
									</div>
								)}
							{showViewMore && (
								<Button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										setShowAllIntervals(true);
									}}
									intent="tertiary"
									className="my-2 ml-4 print:hidden"
								>
									{parkingOption.viewMore(hiddenCount)}
								</Button>
							)}
						</div>
					);
				})
			)}
		</div>
	);
};

const announcedInterval = ({
	interval,
	timezone,
	isInstant,
	parkingOption,
	locale,
}: {
	interval: {
		type: PermitIntervalType;
		startDateTimeISO: string;
		endDateTimeISO: string | undefined;
		times: { startTime: string; endTime: string | null | undefined }[];
		hasSchedule: boolean;
	};
	timezone: string;
	isInstant: boolean;
	parkingOption: ReturnType<typeof useDesignSystemI18n>["parkingOption"];
	locale: string;
}) => {
	if (interval.type === PERMIT_INTERVAL_TYPE.SINGLE) {
		return [
			formatDate({ date: interval.startDateTimeISO, formatType: "Long_Date", timezone, language: locale }),
			...interval.times.map((x) =>
				formatDateRange({
					startDate: x.startTime,
					endDate: x.endTime ?? undefined,
					formatType: "Abbreviated_Time",
					timezone,
					language: locale,
				}),
			),
		].join(", ");
	}
	const start = isInstant
		? parkingOption.startsNow
		: announcedDate({
				dateTimeISO: interval.startDateTimeISO,
				timezone,
				hasSchedule: interval.hasSchedule,
				locale,
			});
	if (!interval.endDateTimeISO) {
		return `${start} – ${parkingOption.noExpiry}`;
	}
	return `${start} – ${announcedDate({ dateTimeISO: interval.endDateTimeISO, timezone, hasSchedule: interval.hasSchedule, locale })}`;
};

const announcedDate = ({
	dateTimeISO,
	timezone,
	hasSchedule,
	locale,
}: {
	dateTimeISO: string;
	timezone: string;
	hasSchedule: boolean;
	locale: string;
}) => {
	const date = formatDate({ date: dateTimeISO, formatType: "Long_Date", timezone, language: locale });
	if (hasSchedule) {
		return date;
	}
	return `${date}, ${formatDate({ date: dateTimeISO, formatType: "Abbreviated_Time", timezone, language: locale })}`;
};
